<template>
  <div class="canvas" ref="canvas" :style="style">
    <f-object v-for="object in renderedObjects" :key="object.id"
      :type="object.data.type"
      v-model="object.data"
      :id="object.id"
      :ownerId="object.ownerId"
      :ownerName="object.ownerName"
      :anchor="anchor"
      :style="objectStyle"
      ref="objects"></f-object>

    <f-cursor v-for="mouse in mice" :key="mouse.key" :name="mouse.userName" :x="mouse.x" :y="mouse.y"></f-cursor>

    <toolbar :settings="settings" :style="toolbarStyle"></toolbar>
    <canvas-settings :settings="settings" v-if="showBoardSettings"></canvas-settings>
  </div>
</template>

<script>
import FObject from "./Object.vue";
import Toolbar from './Toolbar.vue'
import CanvasSettings from './CanvasSettings.vue';
import Cursor from './Cursor.vue'

const { v4: uuid } = require('uuid');

const MOUSE_DISSAPEAR_DURATION = 1000;

export default {
  components: {
    "f-object": FObject,
    'toolbar': Toolbar,
    'canvas-settings': CanvasSettings,
    'f-cursor': Cursor
  },
  props: {
    /**
      * Initial data for the board (fetched from persistance)
      * From here on out the canvas updates its own state using sockets
     */
    data: {
      type: Object,
      required: true
    },
    width: {
      type: Number,
      required: true
    },
    height: {
      type: Number,
      required: true
    }
  },
  data() {
    return {
      userName: localStorage.getItem('userName') || 'Anonymous',
      userId: localStorage.getItem('userId'),
      settings: {},
      anchor: JSON.parse(localStorage.getItem('anchorPosition')) || {
        x: 200,
        y: 200
      },
      holdInterval: null,
      objects: [],
      isHolding: false,
      isScrolling: false,
      scrollAnchor: {
        x: 0,
        y: 0
      },
      showBoardSettings: false,
      mouseUpdateTimeout: false,
      mice: [],
      miceClearTimeouts: {},
    };
  },
  methods: {
    createObject(object) {
      const data = {
          ownerId: this.userId,
          ownerName: this.userName,
          id: uuid(),
          data: object
      }
      this.connection.emit('object_create', data);

      this.onObjectCreated(data)
    },
    onObjectCreated(object) {
      this.objects.push(object);
    },
    onObjectUpdated({ id, changes }) {
      const objectIndex = this.objects.map(object => object.id).indexOf(id);

      if (objectIndex === -1) return

      this.objects[objectIndex].data = {
        ...this.objects[objectIndex].data,
        ...changes,
      };

      const objectToUpdate = this.$refs.objects.find(object => object.id === id);

      objectToUpdate.applyUpdate(changes);
    },
    deleteObject(id) {
      this.connection.emit('object_delete', { id });

      this.onObjectDeleted({ id });
    },
    onObjectDeleted({ id }) {
      const objectIndex = this.objects.map(object => object.id).indexOf(id);

      if (objectIndex === -1) return

      this.objects.splice(objectIndex, 1);
    },
    onSettingsUpdated(e) {
      this.settings = e;
    },
    onMouseUpdated(e) {
      const { userName, x, y } = e;

      const index = this.mice.findIndex(mouse => mouse.userName === userName);

      if (index !== -1) this.$set(this.mice, index, {
        userName,
        x: this.anchor.x - x,
        y: this.anchor.y - y,
        key: Math.random()
      });
      else this.mice.push({
        userName,
        x: this.anchor.x - x,
        y: this.anchor.y - y,
        key: 1
      });

      if (this.miceClearTimeouts[userName])
        clearInterval(this.miceClearTimeouts[userName]);

      this.miceClearTimeouts[userName] = setTimeout(() => {
        const index = this.mice.findIndex(mouse => mouse.userName === userName);

        if (index !== -1) this.mice.splice(index, 1);
      }, MOUSE_DISSAPEAR_DURATION);
    },
    onClickObject(e) {},
    onMouseDown(e) {
      const { clientX: x, clientY: y } = e;

      this.isHolding = true;

      if (this.$refs.objects) {
        const holdOnObjects = this.$refs.objects.filter(object => this.helper.coordinateIsInElement(x, y, object.$refs.holdon));

        if (holdOnObjects.length > 0) {
          const objectOnTop = holdOnObjects.find(object => object.value.zIndex === 1) || holdOnObjects[0]
          objectOnTop.startHold(x, y);
        }

        const resizeObjects = this.$refs.objects.filter(object => this.helper.coordinateIsInElement(x, y, object.$refs.resize));

        if (resizeObjects.length > 0) {
          const objectOnTop = resizeObjects.find(object => object.value.zIndex === 1) || resizeObjects[0]
          objectOnTop.startResize(x, y);
        }
      }

      if (e.srcElement === this.$refs.canvas) {
        this.isScrolling = true;
        this.scrollAnchor = {
          x: this.anchor.x - x,
          y: this.anchor.y - y
        }
      }
    },
    onMouseUp(e) {
      const { clientX: x, clientY: y } = e;

      this.isHolding = false;

      if (this.$refs.objects)
          this.$refs.objects.forEach(object => object.endHold(x, y))

      this.isScrolling = false;
      this.scrollAnchor = this.anchor
    },
    onMouseMove(e) {
      const { clientX: x, clientY: y } = e;

      if (this.isHolding) this.onMouseHold(x, y);

      if (this.mouseUpdateTimeout || this.showBoardSettings) return;

      this.connection.emit('mouse_update', {
        userName: this.userName,
        x: this.anchor.x - x,
        y: this.anchor.y - y
      })

      this.mouseUpdateTimeout = true;

      setTimeout(() => {
        this.mouseUpdateTimeout = false;
      }, 100);
    },
    onMouseHold(x, y) {
      if (this.$refs.objects)
          this.$refs.objects.forEach(object => object.onHold(x, y))

      if (this.isScrolling) {
        this.anchor = {
          x: this.scrollAnchor.x + x,
          y: this.scrollAnchor.y + y
        }
      }
    },
    async initializeObjects(objects) {
      for (let i = 0; i < objects.length; i++) {
        this.objects.push(objects[i]);

        await new Promise(r => setTimeout(r, 10));
      }
    },
    applySettings() {

    },
  },
  computed: {
    renderedObjects: function() {

      
      return this.objects.filter(object => {

        if (-this.anchor.x > object.data.x + object.data.width) return false;
        if (-this.anchor.x + this.width < object.data.x) return false;
        
        if (-this.anchor.y > object.data.y + object.data.height) return false;

        if (-this.anchor.y + this.height < object.data.y) return false;
        
        return true;
      });
    },
    style: function() {
      return `
        width:${this.width}px;
        height:${this.height}px;
        background-color: ${this.settings.background.colour};
        ${this.settings.background.customStyling}
      `;
    },
    objectStyle: function() {
      return `
        ${this.showBoardSettings ? 'filter: blur(10px);' : ''}
      `;
    },
    toolbarStyle: function() {
      return `
        ${this.showBoardSettings ? 'filter: blur(10px);' : ''}
      `;
    }
  },
  mounted() {
    const vue = this;
    
    this.$refs.canvas.addEventListener("mousedown", vue.onMouseDown);
    this.$refs.canvas.addEventListener("mouseup", vue.onMouseUp);
    this.$refs.canvas.onmousemove = vue.onMouseMove
    this.$refs.canvas.addEventListener('contextmenu', event => event.preventDefault());

    this.connection.setCanvas(this);

    this.applySettings();

    setInterval(() => {
      localStorage.setItem('anchorPosition', JSON.stringify(this.anchor))
    }, 3000);

  },
  created() {
    const { objects, settings } = this.data;

    this.settings = settings;

    this.initializeObjects(objects);
  }
};
</script>

<style scoped>
.canvas {
  position: relative;
  overflow: hidden;

  cursor: grab;
}
</style>



// WEBPACK FOOTER //
// src/components/Canvas.vue